import { Box, ThemeSpaceVariable } from "@caspeco/casper-ui-library";
import {
    useFilteredAvailableTimes,
    useFilteredExperiences,
} from "api/api-hooks/use-available-times";
import ExperienceTimeSection from "components/selection-components/select-time/experience-time-section";
import InformationOnlyExperience from "components/selection-components/select-time/information-only-experience";
import SectionOfAvailableTimes from "components/selection-components/select-time/section-of-available-times";
import { useBookingStateContext } from "hooks/use-booking-state";
import { PageType } from "logger/page-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { useMemo } from "react";
import { isSectionShowable } from "utils/availability-utils";

function AvailableTimes() {
    const { date } = useBookingStateContext();
    const availableTimes = useFilteredAvailableTimes(date);
    const experiences = useFilteredExperiences(date);
    const visibleSections = useMemo(
        () => availableTimes.filter((section) => isSectionShowable(section.availabilityState)),
        [availableTimes]
    );

    // Render experience sections
    const experienceSections = experiences
        .filter((experience) => !experience.unavailable)
        .map((experience, index) =>
            experience.informationOnly ? (
                <InformationOnlyExperience
                    key={`experience-${experience.id}`}
                    experience={experience}
                    isFirstExperience={index === 0}
                />
            ) : (
                <ExperienceTimeSection
                    key={`experience-${experience.id}`}
                    experience={experience}
                    isFirstExperience={index === 0}
                />
            )
        );

    // Render regular time sections
    const sections = visibleSections.map((section) => (
        <SectionOfAvailableTimes key={`section${section.id}`} availableTimesSection={section} />
    ));

    const timeSlotCount = useMemo(() => {
        return visibleSections.reduce((mainCounter, section) => {
            const interactiveCount = section.timeSets.reduce((acc: number, timeSet) => {
                return acc + (timeSet.times?.length || 0);
            }, 0);
            return mainCounter + interactiveCount;
        }, 0);
    }, [visibleSections]);

    // Track activity time selection (normal flow)
    usePageTracking({
        pageName: "SelectActivityTime",
        pageType: PageType.Page,
        metadata: {
            activityNames: visibleSections.map((section) => section.name),
            activityIds: visibleSections.map((section) => section.id.toString()),
            isEditMode: false,
            timeSlotCount: timeSlotCount,
        },
    });

    return (
        <Box px={ThemeSpaceVariable.Medium} className="available-times">
            {experienceSections}
            {sections}
        </Box>
    );
}

export default AvailableTimes;
